import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getManagerFeedback } from '../api/services';

const ManagerFeedbackModal = ({ open, onClose }) => {
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch manager feedback when modal opens
  useEffect(() => {
    if (open) {
      fetchManagerFeedback();
    }
  }, [open]);

  const fetchManagerFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getManagerFeedback();
      console.log('Manager Feedback Response:', response); // Log to see the actual response structure
      
      // Handle the response structure - adjust based on your actual API response
      let feedbackData = [];
      
      if (response) {
        // Check if response has data property
        if (response.data && Array.isArray(response.data)) {
          feedbackData = response.data;
        }
        // Check if response is directly an array
        else if (Array.isArray(response)) {
          feedbackData = response;
        }
        // Check if response has content property (paginated response)
        else if (response.content && Array.isArray(response.content)) {
          feedbackData = response.content;
        }
        // Check if response has data.data structure (like your previous API)
        else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          feedbackData = response.data.data;
        }
      }
      
      setFeedbackData(feedbackData);
    } catch (err) {
      setError('Failed to fetch manager feedback. Please try again.');
      console.error('Error fetching manager feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  const getReviewedChip = (reviewed) => {
    // Handle different possible values for reviewed status
    const isReviewed = reviewed === true || reviewed === 'Yes' || reviewed === 'yes' || reviewed === 'Y';
    
    return isReviewed ? (
      <Chip 
        label="Yes" 
        color="success" 
        size="small"
        variant="outlined"
        sx={{ fontWeight: 'bold' }}
      />
    ) : (
      <Chip 
        label="No" 
        color="default" 
        size="small"
        variant="outlined"
      />
    );
  };

  const getFinalPointsChip = (points, reviewed) => {
    // Handle different possible values for reviewed status
    const isReviewed = reviewed === true || reviewed === 'Yes' || reviewed === 'yes' || reviewed === 'Y';
    
    if (!isReviewed || points === null || points === undefined || points === 0) {
      return <Chip label="Pending" color="default" size="small" variant="outlined" />;
    }
    
    return (
      <Chip 
        label={points} 
        color="primary" 
        size="small"
        variant="filled"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!Array.isArray(feedbackData) || feedbackData.length === 0) {
      return {
        total: 0,
        reviewed: 0,
        pending: 0,
        totalPoints: 0
      };
    }

    const reviewed = feedbackData.filter(item => {
      const isReviewed = item.reviewed === true || item.reviewed === 'Yes' || item.reviewed === 'yes' || item.reviewed === 'Y';
      return isReviewed;
    });

    const totalPoints = reviewed.reduce((sum, item) => {
      const points = item.finalPoints || item.points || item.totalPoints || 0;
      return sum + (typeof points === 'number' ? points : parseFloat(points) || 0);
    }, 0);

    return {
      total: feedbackData.length,
      reviewed: reviewed.length,
      pending: feedbackData.length - reviewed.length,
      totalPoints: totalPoints
    };
  };

  const summary = getSummaryStats();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          minHeight: '60vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 3,
        backgroundColor: '#009db9',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
          Manager Feedback
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Review your tasks and manager feedback for the current cycle.
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#009db9' }} />
            </Box>
          ) : !Array.isArray(feedbackData) || feedbackData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: '#333', mb: 2 }}>
                No feedback data available for the current cycle.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
              >
                <Table sx={{ minWidth: 650 }} aria-label="manager feedback table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', backgroundColor: '#009db9', color: '#ffffff' }}>Ticket Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', backgroundColor: '#009db9', color: '#ffffff' }} align="center">Reviewed</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', backgroundColor: '#009db9', color: '#ffffff' }}>Manager's Comment</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', backgroundColor: '#009db9', color: '#ffffff' }} align="center">Final Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedbackData.map((row, index) => {
                      // Map API fields to expected field names
                      const ticketName = row.ticketName || row.name || row.title || row.ticket || `Ticket ${index + 1}`;
                      const reviewed = row.reviewed !== undefined ? row.reviewed : (row.reviewedStatus || row.isReviewed || false);
                      const managerComment = row.managerComment || row.comment || row.feedback || row.managerFeedback || '';
                      const finalPoints = row.finalPoints || row.points || row.totalPoints || row.score || 0;
                      
                      return (
                        <TableRow
                          key={row.id || row.ticketId || row.feedbackId || index}
                          sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            '&:hover': { backgroundColor: '#f8f9fa' }
                          }}
                        >
                          <TableCell 
                            component="th" 
                            scope="row"
                            sx={{ 
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {ticketName}
                          </TableCell>
                          <TableCell align="center">
                            {getReviewedChip(reviewed)}
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontStyle: managerComment ? 'normal' : 'italic',
                                color: managerComment ? 'text.primary' : 'text.secondary',
                                maxWidth: 300,
                                wordWrap: 'break-word'
                              }}
                            >
                              {managerComment || 'No comment yet'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {getFinalPointsChip(finalPoints, reviewed)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary Section */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#009db9', fontWeight: 'bold' }}>
                  Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    <strong>Total Tasks:</strong> {summary.total}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Reviewed:</strong> {summary.reviewed}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Pending Review:</strong> {summary.pending}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Final Points:</strong> {summary.totalPoints}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            px: 3,
            borderColor: '#009db9',
            color: '#009db9',
            '&:hover': {
              backgroundColor: '#e6f7ff',
              borderColor: '#006978'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagerFeedbackModal;